import * as React from "react"
import { parsePhoneNumberFromString, AsYouType } from "libphonenumber-js"

import { cn } from "components/ui/utils"
import { Input } from "components/ui/input"

// Format stored E.164 value → display as (555) 123-4567
const formatPhone = (value: string): string => {
  if (!value) return ""
  const formatter = new AsYouType("US")
  formatter.input(value)
  return formatter.getNumber() ? formatter.getNumber()!.formatNational() : value
}

// Parse user input → E.164 for storage
const parsePhone = (value: string): string => {
  if (!value) return ""
  const parsed = parsePhoneNumberFromString(value, "US")
  return parsed ? parsed.format("E.164") : value
}

// Validate US phone number — returns error message or undefined
const validateUSPhone = (message: string) => (value: string) => {
  if (!value) return undefined
  const parsed = parsePhoneNumberFromString(value, "US")
  if (!parsed || !parsed.isValid() || parsed.country !== "US") {
    return message
  }
  return undefined
}

interface PhoneInputProps extends Omit<React.ComponentProps<"input">, "onChange" | "value"> {
  value?: string
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void
}

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ className, value = "", ...props }, ref) => {
    const display = formatPhone(value)

    return (
      <Input
        ref={ref}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        placeholder="(555) 123-4567"
        className={cn(className)}
        {...props}
        value={display}
      />
    )
  }
)
PhoneInput.displayName = "PhoneInput"

export { PhoneInput, formatPhone, parsePhone, validateUSPhone }
